import { OrderConfirmationItem } from '../_entities/purchase-order/order-confirmation-item';

/**
 * Consolidates order confirmation items that share the same part number, price,
 * job number, and delivery option into a single line item with summed quantities
 * and extended prices.
 *
 * This handles CumminsVinRequired parts which are stored as individual items
 * (each with quantity=1) in the cart and order, but should display as a single
 * consolidated line item on the order confirmation page — consistent with
 * how the my-cart page groups them via groupCumminsVinItems.
 *
 * @param items - Array of order confirmation items to consolidate
 * @returns Array of consolidated order confirmation items
 */
export function consolidateOrderConfirmationItems(
  items: OrderConfirmationItem[]
): OrderConfirmationItem[] {
  if (!items?.length) {
    return [];
  }

  const groupMap = new Map<string, OrderConfirmationItem[]>();

  for (const item of items) {
    const key = `${item.partNumOnly}|${item.finalPrice}|${item.jobNumber ?? ''}|${item.deliveryOption ?? ''}`;
    const group = groupMap.get(key);
    if (group) {
      group.push(item);
    } else {
      groupMap.set(key, [item]);
    }
  }

  const result: OrderConfirmationItem[] = [];

  for (const group of groupMap.values()) {
    if (group.length === 1) {
      result.push(group[0]);
    } else {
      const totalQuantity = group.reduce((sum, item) => sum + item.quantity, 0);
      const totalExtendedPrice = group.reduce((sum, item) => sum + item.extendedPrice, 0);
      const totalQuantityConfirm = group.reduce((sum, item) => sum + item.quantityConfirm, 0);
      result.push({
        ...group[0],
        quantity: totalQuantity,
        extendedPrice: totalExtendedPrice,
        quantityConfirm: totalQuantityConfirm
      });
    }
  }

  return result;
}
